import React, { useEffect, useState } from "react";
import "./TechBackground.css";

interface TechIcon {
  id: number;
  src: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  scale: number;
}

const TechBackground: React.FC = () => {
  const [icons, setIcons] = useState<TechIcon[]>([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const iconPaths = [
    "/icons/android.svg",
    "/icons/angular (1).svg",
    "/icons/angular.svg",
    "/icons/aws-cloudfront.svg",
    "/icons/aws-cloudsearch.svg",
    "/icons/aws-codedeploy.svg",
    "/icons/aws-codestar.svg",
    "/icons/aws-cognito.svg",
    "/icons/aws-eks.svg",
    "/icons/aws-elb.svg",
    "/icons/aws-iam.svg",
    "/icons/aws-lambda.svg",
    "/icons/aws.svg",
    "/icons/cloudflare-icon.svg",
    "/icons/cloudflare.svg",
    "/icons/digital-ocean.svg",
    "/icons/docker-icon.svg",
    "/icons/express.svg",
    "/icons/fastapi-icon.svg",
    "/icons/fastapi.svg",
    "/icons/firebase.svg",
    "/icons/flutter.svg",
    "/icons/github-actions.svg",
    "/icons/github-icon.svg",
    "/icons/gitlab-icon.svg",
    "/icons/gitlab.svg",
    "/icons/google-cloud.svg",
    "/icons/google-play-console-icon.svg",
    "/icons/google-play-console.svg",
    "/icons/grafana.svg",
    "/icons/java.svg",
    "/icons/javascript.svg",
    "/icons/jenkins.svg",
    "/icons/kafka.svg",
    "/icons/kops.svg",
    "/icons/kotlin-icon.svg",
    "/icons/kubernetes.svg",
    "/icons/microsoft-azure.svg",
    "/icons/mysql-icon.svg",
    "/icons/nginx.svg",
    "/icons/ngrok.svg",
    "/icons/nodejs-icon.svg",
    "/icons/postgresql.svg",
    "/icons/prometheus.svg",
    "/icons/python.svg",
    "/icons/rabbitmq.svg",
    "/icons/react.svg",
    "/icons/redis.svg",
    "/icons/tailwindcss-icon.svg",
    "/icons/typescript-icon.svg",
    "/icons/vagrant.svg",
    "/icons/vercel.svg",
  ];

  useEffect(() => {
    // Initialize icons with random positions and velocities
    const initialIcons: TechIcon[] = iconPaths.map((src, index) => ({
      id: index,
      src,
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 2,
      rotation: Math.random() * 360,
      scale: 0.8 + Math.random() * 0.4
    }));

    setIcons(initialIcons);

    // Mouse move handler for interactive effects
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Animation loop
    let animationId: number;
    const animate = () => {
      setIcons(prevIcons => 
        prevIcons.map(icon => {
          let newX = icon.x + icon.vx;
          let newY = icon.y + icon.vy;
          let newVx = icon.vx;
          let newVy = icon.vy;

          // Bounce off edges
          if (newX <= 0 || newX >= window.innerWidth) {
            newVx = -newVx;
            newX = Math.max(0, Math.min(window.innerWidth, newX));
          }
          if (newY <= 0 || newY >= window.innerHeight) {
            newVy = -newVy;
            newY = Math.max(0, Math.min(window.innerHeight, newY));
          }

          // Mouse interaction - slight attraction
          const dx = mousePosition.x - icon.x;
          const dy = mousePosition.y - icon.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 7000 && distance > 0) {
            newVx += (dx / distance) * 0.002;
            newVy += (dy / distance) * 0.001;
          }

          // Limit velocity
          const speed = Math.sqrt(newVx * newVx + newVy * newVy);
          if (speed > 3) {
            newVx = (newVx / speed) * 3;
            newVy = (newVy / speed) * 3;
          }

          return {
            ...icon,
            x: newX,
            y: newY,
            vx: newVx,
            vy: newVy,
            rotation: icon.rotation + 0.5,
            scale: 0.8 + Math.sin(Date.now() * 0.001 + icon.id) * 0.2
          };
        })
      );

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [mousePosition.x, mousePosition.y]);

  return (
    <div className="tech-background">
      {icons.map(icon => (
        <div
          key={icon.id}
          className="tech-icon"
          style={{
            left: `${icon.x}px`,
            top: `${icon.y}px`,
            transform: `rotate(${icon.rotation}deg) scale(${icon.scale})`,
            zIndex: Math.floor(icon.y)
          }}
        >
          <img 
            src={icon.src} 
            alt="tech" 
            className="icon-image"
            onError={(e) => {
              console.log(`Failed to load icon: ${icon.src}`);
              // Fallback to a colored div if image fails
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.parentElement!.innerHTML = '<div class="icon-fallback"></div>';
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default TechBackground;
